/**
 * Watcher global da fila de conquistas — irmão do `LeagueWeekRecapWatcher`.
 *
 * Montado uma única vez em `AppRoutes`. Sem auth, é no-op: a fila só drena
 * para quem está logado, e o cartão precisa do `uid` para descobrir o flair.
 *
 * Toda a decisão de "quando" vive em `useBadgeUnlockQueue` (rota segura, gate
 * de contexto crítico, assentamento). Aqui é só a montagem.
 */

import { lazy, Suspense } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useBadgeUnlockQueue } from "../hooks/useBadgeUnlockQueue";
import { useSuppressCelebrations } from "../utils/celebrationGate";

// Lazy: o cartão (15 KB de fonte + catálogo de recompensas) só é desenhado
// quando há conquista nova; fora do entry (17/09).
const BadgeUnlockModal = lazy(() =>
  import("./BadgeUnlockModal").then((m) => ({ default: m.BadgeUnlockModal })),
);

export function BadgeUnlockWatcher() {
  const { user, profile } = useAuth();
  const { batch, dismiss } = useBadgeUnlockQueue(!!user);

  // O cartão declara o próprio bloqueio: ele e o convite de push disputam o
  // mesmo instante (primeira rota segura depois do login) e não podem empilhar.
  // Não se auto-bloqueia — `useBadgeUnlockQueue` lê o gate só para ABRIR, e com
  // um lote aberto o efeito nem chega lá.
  useSuppressCelebrations(batch.length > 0, "badge-unlock");

  if (!user || batch.length === 0) return null;

  return (
    <Suspense fallback={null}>
    <BadgeUnlockModal
      badgeIds={batch}
      uid={user.uid}
      username={profile?.username ?? profile?.displayName ?? "leitor"}
      themeId={profile?.themeId ?? profile?.themePreference}
      onClose={dismiss}
    />
    </Suspense>
  );
}
